import { ApiClient, RealtimeClient } from '@momonos/api-client'
import { KDS_COLUMNS, type KdsColumn, nextKitchenStatus, type Order } from '@momonos/shared'
import { Button, cn, FullscreenLoader } from '@momonos/ui'
import { Volume2, WifiOff } from 'lucide-react'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { OrderCard } from './components/OrderCard'
import { boardReducer, columnsOf, emptyBoard } from './lib/board'
import { API_URL, BRANCH_ID, REALTIME_URL } from './lib/config'
import { kitchenSound } from './lib/sound'

const COLUMN_TITLES: Record<KdsColumn, string> = {
  new: 'New Orders',
  preparing: 'Preparing',
  ready: 'Ready',
  delivered: 'Delivered',
}

const COLUMN_ACCENT: Record<KdsColumn, string> = {
  new: 'border-t-kds-new',
  preparing: 'border-t-kds-preparing',
  ready: 'border-t-kds-ready',
  delivered: 'border-t-kds-delivered',
}

export function App() {
  const [board, dispatch] = useReducer(boardReducer, emptyBoard)
  const [connection, setConnection] = useState<string>('connecting')
  const [shiftStarted, setShiftStarted] = useState(false)
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [token, setToken] = useState<string | null>(null)

  const alarmed = useRef(new Set<string>())

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: API_URL,
        getAccessToken: () => token,
        onTokenRefreshed: setToken,
      }),
    [token],
  )

  /** FR-RT-4 — authoritative state, pulled before events resume. */
  const snapshot = useCallback(async () => {
    const data = await api.get<{ orders: Order[] }>('/v1/orders', {
      query: { branch: BRANCH_ID, status: 'open' },
    })
    dispatch({ type: 'snapshot', orders: data.orders })
  }, [api])

  useEffect(() => {
    if (!shiftStarted) return

    const client = new RealtimeClient({
      url: `${REALTIME_URL}/branch/${BRANCH_ID}`,
      branchId: BRANCH_ID,
      role: 'kds',
      getAccessToken: () => token,
      onSnapshot: snapshot,
      onEvent: (event) => dispatch({ type: 'event', event }),
      onStateChange: setConnection,
    })

    void client.connect()
    return () => client.disconnect()
  }, [shiftStarted, snapshot, token])

  // One ticking clock for every countdown, rather than a timer per card.
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  // FR-KDS-3 — chime for a new order, then clear the flag so it fires once.
  useEffect(() => {
    if (board.unseen.length === 0) return

    kitchenSound.newOrder()
    for (const orderId of board.unseen) dispatch({ type: 'acknowledge', orderId })
  }, [board.unseen])

  // FR-KDS-3 — alarm when a countdown hits zero, once per order.
  useEffect(() => {
    for (const order of Object.values(board.orders)) {
      const overdue =
        (order.prepStartedAt ?? order.placedAt) + (order.estimatedWaitSeconds ?? 0) * 1000 < now
      if (overdue && !alarmed.current.has(order.id) && order.status !== 'ready') {
        alarmed.current.add(order.id)
        kitchenSound.overdue()
      }
    }
  }, [board.orders, now])

  const advance = useCallback(
    async (order: Order) => {
      const next = nextKitchenStatus(order.status)
      if (!next) return

      setBusyOrderId(order.id)
      // Optimistic: the card must move the instant it is tapped, even on a
      // slow tablet. The next snapshot corrects it if the server disagrees.
      dispatch({ type: 'optimistic-status', orderId: order.id, status: next })

      try {
        await api.patch(`/v1/orders/${order.id}/status`, { status: next })
      } catch {
        await snapshot()
      } finally {
        setBusyOrderId(null)
      }
    },
    [api, snapshot],
  )

  if (!shiftStarted) {
    return <StartShift onStart={() => setShiftStarted(true)} />
  }

  if (connection === 'connecting') {
    return <FullscreenLoader label="Connecting to the kitchen" />
  }

  const columns = columnsOf(board)

  return (
    <div className="flex min-h-dvh flex-col bg-surface touch-surface">
      <header className="flex items-center justify-between border-surface-border border-b px-6 py-3">
        <h1 className="font-bold text-content text-xl">Momonos Kitchen</h1>

        {connection !== 'connected' && (
          <p className="flex items-center gap-2 rounded-full bg-momo-red/15 px-3 py-1 text-momo-red text-sm">
            <WifiOff aria-hidden="true" className="size-4" />
            Reconnecting…
          </p>
        )}
      </header>

      <div className="grid flex-1 grid-cols-4 gap-3 p-3">
        {KDS_COLUMNS.map((column) => (
          <section
            key={column}
            aria-label={COLUMN_TITLES[column]}
            className={cn(
              'flex flex-col rounded-xl border-surface-border border-t-4 bg-surface-raised/40',
              COLUMN_ACCENT[column],
            )}
          >
            <h2 className="flex items-center justify-between px-4 py-3 font-semibold text-content">
              {COLUMN_TITLES[column]}
              <span className="rounded-full bg-surface-overlay px-2.5 py-0.5 text-content-muted text-sm tabular">
                {columns[column].length}
              </span>
            </h2>

            <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-3">
              {columns[column].map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  now={now}
                  isUnseen={board.unseen.includes(order.id)}
                  isBusy={busyOrderId === order.id}
                  onAdvance={advance}
                />
              ))}

              {columns[column].length === 0 && (
                <p className="px-2 py-8 text-center text-content-muted text-sm">Nothing here</p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/**
 * FR-KDS-3 — browsers refuse to play audio before a user gesture, so a shift
 * starts with a deliberate tap that arms the chime and the alarm.
 */
function StartShift({ onStart }: { onStart: () => void }) {
  const [arming, setArming] = useState(false)

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-surface p-8 text-center">
      <h1 className="font-bold text-4xl text-content">Momonos Kitchen</h1>
      <p className="max-w-md text-content-secondary">
        Tap to start the shift. This turns on the new-order chime and the late-order alarm.
      </p>

      <Button
        size="kiosk"
        isLoading={arming}
        onClick={async () => {
          setArming(true)
          // Ignore the result: a tablet with no audio must still show orders.
          await kitchenSound.arm()
          onStart()
        }}
      >
        <Volume2 aria-hidden="true" className="size-6" />
        Start shift
      </Button>
    </main>
  )
}
